/**
 * Export handlers for the post-season screens (G2.4, G2 test *d*).
 *
 * One per data route, each returning the CURRENT view — G2.4's fidelity rule — and each
 * carrying all nine provenance fields, which the type makes non-optional (G2 test *e*).
 *
 * Every handler reads the SAME pinned operation the screen reads, through `collectPages`
 * and `callOperation`, so an export cannot report a different perimeter from the table it
 * came from. The season is read from the URL by the same function the screens use, because
 * the perimeter is URL state (G2.2) and an export that quietly widened it would be the
 * unbounded read G16 removed, arriving by a different door.
 *
 * **THE DEMAND BASIS TRAVELS WITH THE EXPORT.** A CSV of unconstrained-demand units is
 * useless — worse, dangerous — without the verdict that says whether those units are
 * recovered demand or censored sales, because the file outlives the screen and the reader
 * of the file was not there. So `demand_basis` is a provenance filter on every export
 * built on demand measures, and a column on the lost-sales rows.
 *
 * **The undefined values leave as a missing state, never as zero.** That is the whole
 * discipline of this chapter carried across the export boundary: a GMROI with no average
 * inventory, an accuracy record over a group with no actuals, a defect rate for a supplier
 * nobody inspected. `ExportCell` has a `missing` kind and no path that carries a display
 * string for a number, so `0` is not reachable by accident. A CSV that rendered them as
 * `0` would put a dead option, an unmeasured group and a flawless supplier into the same
 * column, and whoever sorted on it would act on it.
 */

import { callOperation, collectPages } from '@/api';
import {
  demandPanel,
  latestRecoveryRun,
  listAccuracy,
  listAttributeIndices,
  listLostSalesPotential,
  listPromotions,
  listRefreshes,
  listSeasonReview,
  listStateCensus,
  listSupplierScorecards,
  vocabulary,
  type AccuracyRow,
  type AttributeIndexRow,
  type PromotionRow,
  type RefreshRow,
  type SeasonReviewRow,
  type SupplierScorecardRow,
} from '@/api/generated';
import type { DemandPanelCell, RecoveryCoverageRow } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import { SIZE_EXPORT_COLUMNS, SIZE_EXPORT_KEYS, sizeExportCells, sizeNameAliased } from '@/planning';
import type { ExportCell, ExportRequest, Provenance } from '@/export';

import { accuracyCategoryLabel } from './api';
import { censusLines, doorsInScope, figureValue, potentialLines, type PotentialFigure } from './lost-sales-lines';
import { panelQuery, readLostSalesScope, type LostSalesScope } from './lost-sales-scope';
import { readSeasonScope } from './season';

/** The build that produced the file — `buildVersion` in `@/export`, `docs/adr/9007`. */
const PLATFORM_VERSION = buildVersion();

/**
 * The perimeter, or a refusal.
 *
 * Thrown rather than defaulted. The season-bounded operations were bounded on purpose
 * (G16), and an export handler that substituted a season would re-open the estate-wide
 * read the bound closed. In practice this is unreachable from the UI — `SeasonGate` does
 * not render the table, and therefore not the export control, without a season — so the
 * throw is a guard on the programmatic path rather than a state a planner meets.
 */
function requireSeason(): string {
  const season = readSeasonScope();
  if (season === null) {
    throw new Error('postseason export requires a season in scope; none is in the URL');
  }
  return season;
}

const provenance = (
  measures: readonly { measure: string; definition: string }[],
  filters: readonly { field: string; operator: 'equals'; value: string }[],
): Provenance => ({
  filters,
  hierarchyNode: NOT_PUBLISHED,
  timeWindow: NOT_PUBLISHED,
  scenario: NOT_PUBLISHED,
  planVersionId: NOT_PUBLISHED,
  measureDefinitions: measures.map((entry) => ({
    measure: entry.measure,
    definition: entry.definition,
    registerVersion: '',
  })),
  generatedAt: new Date().toISOString(),
  generatedBy: NO_PRINCIPAL,
  platformVersion: PLATFORM_VERSION,
});

/**
 * A number that may be undefined, as an export cell. `null` becomes a missing state and
 * never a zero — the single most important line in this file.
 */
function numeric(value: number | null): ExportCell {
  return value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value };
}

function integer(value: number | null): ExportCell {
  return value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'integer', raw: value };
}

/**
 * What an export can say about the season's recovery WITHOUT the demand panel — production,
 * 2026-09-25.
 *
 * `GET /postseason/recovery/demand-panel` requires `locationKeys` and `fiscalWeeks`, so asking it
 * with the season alone answers 422 and failed both exports outright. The basis and the cells
 * are stated as NOT READ, with the reason, rather than guessed — a basis written into a
 * provenance sheet is a claim about every row in it.
 *
 * No run is named either (ADR 8002). This used to take the run from `/recovery/latest`, but since
 * bata-platform v0.55.0 that names the newest run in force at ONE door, and a season is recovered
 * a few doors per run: naming it here would describe a handful of doors as the season.
 */
const PANEL_NOT_READ = 'not_read_doors_and_weeks_unnamed';

interface PanelIdentity {
  readonly cells: readonly DemandPanelCell[];
  readonly demandBasis: string;
  readonly weeksWithoutUnconstrainedDemand: string;
  /** Every run the cells came from, sorted — a panel is read per door from the run in force there. */
  readonly recoveryRunIds: readonly string[];
  readonly methodVersions: readonly string[];
}

function panelWithoutScope(): PanelIdentity {
  return {
    cells: [],
    demandBasis: PANEL_NOT_READ,
    weeksWithoutUnconstrainedDemand: PANEL_NOT_READ,
    recoveryRunIds: [],
    methodVersions: [],
  };
}

export async function seasonReviewExport(): Promise<ExportRequest> {
  const season = requireSeason();
  const all = await collectPages(listSeasonReview, { query: {} });
  return seasonReviewRequest(all.filter((row) => row.season === season), season, panelWithoutScope());
}

/**
 * The CSV under the season-review table: the rows of the table's view, with the same season
 * and the same panel identity the route's export reads (`docs/adr/9008`, `docs/adr/9009`).
 */
export async function seasonReviewViewExport(view: readonly SeasonReviewRow[]): Promise<ExportRequest> {
  const season = requireSeason();
  return seasonReviewRequest(view, season, panelWithoutScope());
}

/** The season review's sheet over rows already read and scoped to `season`, in the order given. */
export function seasonReviewRequest(
  scoped: readonly SeasonReviewRow[],
  season: string,
  panel: { readonly demandBasis: string; readonly weeksWithoutUnconstrainedDemand: string },
): ExportRequest {
  const rows: ExportCell[][] = scoped.map((row) => [
    { kind: 'text', value: row.optionKey },
    { kind: 'text', value: row.clusterKey },
    { kind: 'text', value: row.channelKey },
    integer(row.grossUnits),
    integer(row.unconstrainedUnits),
    integer(row.recoveredUnits),
    numeric(row.demandCaptureRatio),
    numeric(row.sellThroughRate),
    numeric(row.fullPriceSellThroughRate),
    numeric(row.markdownRate),
    numeric(row.gmroi),
    integer(row.terminalStockUnits),
  ]);
  return {
    id: 'postseason.review.table',
    titleKey: 'postseason.review.title',
    columns: [
      { key: 'optionKey', headerKey: 'postseason.column.option', descriptionKey: 'postseason.columnGuide.review.optionKey', kind: 'text' },
      { key: 'clusterKey', headerKey: 'postseason.column.cluster', descriptionKey: 'postseason.columnGuide.review.clusterKey', kind: 'text' },
      { key: 'channelKey', headerKey: 'postseason.column.channel', descriptionKey: 'postseason.columnGuide.review.channelKey', kind: 'text' },
      { key: 'grossUnits', headerKey: 'postseason.measure.unitsSold', descriptionKey: 'postseason.columnGuide.review.grossUnits', kind: 'integer', unit: 'units', polarity: 'higher-is-better' },
      { key: 'unconstrainedUnits', headerKey: 'postseason.measure.unconstrainedUnits', descriptionKey: 'postseason.columnGuide.review.unconstrainedUnits', kind: 'integer', unit: 'units', polarity: 'higher-is-better' },
      { key: 'recoveredUnits', headerKey: 'postseason.measure.recoveredUnits', descriptionKey: 'postseason.columnGuide.review.recoveredUnits', kind: 'integer', unit: 'units', polarity: 'lower-is-better' },
      { key: 'demandCaptureRatio', headerKey: 'postseason.measure.demandCapture', descriptionKey: 'postseason.columnGuide.review.demandCapture', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'sellThroughRate', headerKey: 'postseason.measure.sellThrough', descriptionKey: 'postseason.columnGuide.review.sellThrough', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'fullPriceSellThroughRate', headerKey: 'postseason.measure.fullPriceSellThrough', descriptionKey: 'postseason.columnGuide.review.fullPriceSellThrough', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'markdownRate', headerKey: 'postseason.measure.markdownRate', descriptionKey: 'postseason.columnGuide.review.markdownRate', kind: 'number', unit: 'ratio', polarity: 'lower-is-better' },
      { key: 'gmroi', headerKey: 'postseason.measure.gmroi', descriptionKey: 'postseason.columnGuide.review.gmroi', kind: 'number', unit: 'index', polarity: 'higher-is-better' },
      { key: 'terminalStockUnits', headerKey: 'postseason.measure.terminalStock', descriptionKey: 'postseason.columnGuide.review.terminalStock', kind: 'integer', unit: 'units', polarity: 'lower-is-better' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [
        { measure: 'units_sold', definition: 'Units sold in the perimeter.' },
        {
          measure: 'unconstrained_demand_units',
          definition:
            'Demand that would have been realised with unlimited availability, reconstructed from observed sales censored by the availability state.',
        },
        {
          measure: 'gmroi',
          definition:
            'Realised gross margin over the mean weekly inventory at cost across the window.',
        },
      ],
      [
        { field: 'season', operator: 'equals', value: season },
        // Not decoration. Whoever opens this file next season has no other way to learn
        // whether the demand column is recovered demand or censored sales.
        { field: 'demand_basis', operator: 'equals', value: panel.demandBasis },
        {
          field: 'weeks_without_unconstrained_demand',
          operator: 'equals',
          value: String(panel.weeksWithoutUnconstrainedDemand),
        },
      ],
    ),
    perimeter: {
      filters: [{ field: 'season', operator: 'equals', value: season }],
      sort: [{ key: 'grossUnits', direction: 'desc' }],
      visibleColumns: [
        'optionKey', 'clusterKey', 'channelKey', 'grossUnits', 'unconstrainedUnits',
        'recoveredUnits', 'demandCaptureRatio', 'sellThroughRate',
        'fullPriceSellThroughRate', 'markdownRate', 'gmroi', 'terminalStockUnits',
      ],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

/**
 * The panel the Lost Sales URL names (`?store=`, `?week=`, `?article=`, F14), read through the
 * same operation and the same parse the screen uses — or, when no doors or no weeks are named,
 * the run's identity with the panel stated as NOT READ, exactly as before.
 */
async function panelInScope(season: string, scope: LostSalesScope): Promise<PanelIdentity> {
  const query = panelQuery(season, scope);
  if (query === null) return panelWithoutScope();
  const panel = await callOperation(demandPanel, { query });
  return {
    cells: panel.cells,
    demandBasis: panel.demandBasis,
    weeksWithoutUnconstrainedDemand: String(panel.weeksWithoutUnconstrainedDemand),
    recoveryRunIds: panelRuns(panel),
    methodVersions: panelMethods(panel),
  };
}

/**
 * Every run a panel's cells came from. `recoveryRunIds` is the list (v0.55.0); the single
 * `recoveryRunId` is empty when the cells came from several runs, so it is only the fallback.
 */
export function panelRuns(panel: { readonly recoveryRunIds?: readonly string[]; readonly recoveryRunId: string }): readonly string[] {
  if (panel.recoveryRunIds !== undefined && panel.recoveryRunIds.length > 0) return panel.recoveryRunIds;
  return panel.recoveryRunId === '' ? [] : [panel.recoveryRunId];
}

/** Every method a panel's cells came from — as `panelRuns`. */
export function panelMethods(panel: { readonly methodVersions?: readonly string[]; readonly methodVersion: string }): readonly string[] {
  if (panel.methodVersions !== undefined && panel.methodVersions.length > 0) return panel.methodVersions;
  return panel.methodVersion === '' ? [] : [panel.methodVersion];
}

/** The scope's named lists as perimeter filters — one per value, so the file states its doors. */
function scopeFilters(scope: LostSalesScope): { field: string; operator: 'equals'; value: string }[] {
  return [
    ...scope.stores.map((value) => ({ field: 'location_key', operator: 'equals' as const, value })),
    ...scope.weeks.map((value) => ({ field: 'fiscal_week', operator: 'equals' as const, value })),
    ...scope.articles.map((value) => ({ field: 'article_key', operator: 'equals' as const, value })),
  ];
}

export async function lostSalesExport(): Promise<ExportRequest> {
  const season = requireSeason();
  const scope = readLostSalesScope(typeof window === 'undefined' ? '' : window.location.search);
  const panel = await panelInScope(season, scope);
  const rows: ExportCell[][] = panel.cells.map((cell) => [
    { kind: 'text', value: cell.articleKey },
    // The size as the platform named it, the key kept as an identifier (ADR 9027).
    ...sizeExportCells(sizeNameAliased(cell)),
    { kind: 'text', value: cell.locationKey },
    { kind: 'text', value: cell.fiscalWeek },
    // The basis on every row, not only in the provenance block. A reader who slices this
    // file in a spreadsheet keeps the rows and loses the header.
    { kind: 'text', value: panel.demandBasis },
    integer(cell.observedUnits),
    integer(cell.recoveredUnits),
    integer(cell.unconstrainedDemandUnits),
    integer(cell.lowerUnits),
    integer(cell.upperUnits),
    { kind: 'text', value: cell.isCensored ? 'censored' : 'observed' },
    // The run the cell was read from: a panel is read per door, from the run in force there.
    { kind: 'text', value: cell.recoveryRunId },
  ]);
  return {
    id: 'postseason.lostSales.table',
    titleKey: 'postseason.lostSales.title',
    columns: [
      { key: 'articleKey', headerKey: 'postseason.column.article', descriptionKey: 'postseason.columnGuide.lostSales.articleKey', kind: 'text' },
      ...SIZE_EXPORT_COLUMNS,
      { key: 'locationKey', headerKey: 'postseason.column.location', descriptionKey: 'postseason.columnGuide.lostSales.locationKey', kind: 'text' },
      { key: 'fiscalWeek', headerKey: 'postseason.column.week', descriptionKey: 'postseason.columnGuide.lostSales.fiscalWeek', kind: 'text' },
      { key: 'demandBasis', headerKey: 'postseason.column.basis', descriptionKey: 'postseason.columnGuide.lostSales.demandBasis', kind: 'text' },
      { key: 'observedUnits', headerKey: 'postseason.measure.observedUnits', descriptionKey: 'postseason.columnGuide.lostSales.observedUnits', kind: 'integer', unit: 'units', polarity: 'higher-is-better' },
      { key: 'recoveredUnits', headerKey: 'postseason.measure.recoveredUnits', descriptionKey: 'postseason.columnGuide.lostSales.recoveredUnits', kind: 'integer', unit: 'units', polarity: 'lower-is-better' },
      { key: 'unconstrainedDemandUnits', headerKey: 'postseason.measure.unconstrainedUnits', descriptionKey: 'postseason.columnGuide.lostSales.unconstrainedUnits', kind: 'integer', unit: 'units', polarity: 'higher-is-better' },
      { key: 'lowerUnits', headerKey: 'postseason.measure.lowerBound', descriptionKey: 'postseason.columnGuide.lostSales.lowerUnits', kind: 'integer', unit: 'units', polarity: 'target-relative' },
      { key: 'upperUnits', headerKey: 'postseason.measure.upperBound', descriptionKey: 'postseason.columnGuide.lostSales.upperUnits', kind: 'integer', unit: 'units', polarity: 'target-relative' },
      { key: 'isCensored', headerKey: 'postseason.column.censored', descriptionKey: 'postseason.columnGuide.lostSales.isCensored', kind: 'text' },
      { key: 'recoveryRunId', headerKey: 'postseason.column.recoveryRun', descriptionKey: 'postseason.columnGuide.lostSales.recoveryRunId', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [
        {
          measure: 'lost_sales_units',
          definition: 'The imputed increment between observed sales and unconstrained demand.',
        },
        {
          measure: 'recovery_holdout_error',
          definition:
            'Weighted absolute percentage error of reconstructed demand against withheld actuals on artificially censored, known-available store-weeks.',
        },
      ],
      [
        { field: 'season', operator: 'equals', value: season },
        { field: 'demand_basis', operator: 'equals', value: panel.demandBasis },
        {
          field: 'weeks_without_unconstrained_demand',
          operator: 'equals',
          value: String(panel.weeksWithoutUnconstrainedDemand),
        },
        // One filter per run and per method, as the doors are stated: a panel read per door
        // can come from several runs, and the file names every one of them.
        ...panel.recoveryRunIds.map((value) => ({ field: 'recovery_run_id', operator: 'equals' as const, value })),
        ...panel.methodVersions.map((value) => ({ field: 'method_version', operator: 'equals' as const, value })),
      ],
    ),
    perimeter: {
      filters: [{ field: 'season', operator: 'equals', value: season }, ...scopeFilters(scope)],
      sort: [{ key: 'recoveredUnits', direction: 'desc' }],
      visibleColumns: [
        'articleKey', ...SIZE_EXPORT_KEYS, 'locationKey', 'fiscalWeek', 'demandBasis',
        'observedUnits', 'recoveredUnits', 'unconstrainedDemandUnits', 'lowerUnits',
        'upperUnits', 'isCensored', 'recoveryRunId',
      ],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

/**
 * The Explorer's coverage table as a CSV — every door that passes the planner's filters, in the
 * planner's rank order, not only the fifty drawn (ADR 9034). One row per door, from the run in
 * force there (`GET /postseason/recovery/coverage`, ADR 8002), so the runs are a column and the
 * file never names one run as the season's.
 */
export function recoveryCoverageRequest(rows: readonly RecoveryCoverageRow[], season: string): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    { kind: 'text', value: row.locationKey },
    { kind: 'text', value: row.countryKey },
    integer(row.recoveredUnits),
    integer(row.observedUnits),
    { kind: 'integer', raw: row.censoredCells },
    { kind: 'integer', raw: row.cells },
    { kind: 'integer', raw: row.weeks },
    { kind: 'text', value: row.firstWeek },
    { kind: 'text', value: row.lastWeek },
    { kind: 'text', value: row.recoveryRunId },
    { kind: 'date', iso: row.generatedAt },
  ]);
  return {
    id: 'postseason.lostSales.coverage',
    titleKey: 'postseason.lostSales.coverage.title',
    columns: [
      { key: 'locationKey', headerKey: 'postseason.column.location', descriptionKey: 'postseason.columnGuide.coverage.locationKey', kind: 'text' },
      { key: 'countryKey', headerKey: 'postseason.column.country', descriptionKey: 'postseason.columnGuide.coverage.countryKey', kind: 'text' },
      { key: 'recoveredUnits', headerKey: 'postseason.measure.recoveredUnits', descriptionKey: 'postseason.columnGuide.coverage.recoveredUnits', kind: 'integer', unit: 'units', polarity: 'lower-is-better' },
      { key: 'observedUnits', headerKey: 'postseason.measure.observedUnits', descriptionKey: 'postseason.columnGuide.coverage.observedUnits', kind: 'integer', unit: 'units', polarity: 'higher-is-better' },
      { key: 'censoredCells', headerKey: 'postseason.lostSales.breakdown.censoredCells', descriptionKey: 'postseason.columnGuide.coverage.censoredCells', kind: 'integer', unit: 'cells' },
      { key: 'cells', headerKey: 'postseason.lostSales.coverage.cells', descriptionKey: 'postseason.columnGuide.coverage.cells', kind: 'integer', unit: 'cells' },
      { key: 'weeks', headerKey: 'postseason.lostSales.coverage.weeks', descriptionKey: 'postseason.columnGuide.coverage.weeks', kind: 'integer', unit: 'weeks' },
      { key: 'firstWeek', headerKey: 'postseason.lostSales.coverage.firstWeek', descriptionKey: 'postseason.columnGuide.coverage.firstWeek', kind: 'text' },
      { key: 'lastWeek', headerKey: 'postseason.lostSales.coverage.lastWeek', descriptionKey: 'postseason.columnGuide.coverage.lastWeek', kind: 'text' },
      { key: 'recoveryRunId', headerKey: 'postseason.column.recoveryRun', descriptionKey: 'postseason.columnGuide.coverage.recoveryRunId', kind: 'text' },
      { key: 'generatedAt', headerKey: 'postseason.lostSales.coverage.generatedAt', descriptionKey: 'postseason.columnGuide.coverage.generatedAt', kind: 'date' },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: provenance(
      [{
        measure: 'recovery_coverage',
        definition: 'Per door, the newest recovery run not superseded that holds cells there: its distinct weeks with a cell, the span first to last, its cells and censored cells, and observed and recovered units summed over its persisted cells. Each door is counted once.',
      }],
      [{ field: 'season', operator: 'equals', value: season }],
    ),
    perimeter: {
      filters: [{ field: 'season', operator: 'equals', value: season }],
      sort: [{ key: 'recoveredUnits', direction: 'desc' }],
      visibleColumns: [
        'locationKey', 'countryKey', 'recoveredUnits', 'observedUnits', 'censoredCells', 'cells',
        'weeks', 'firstWeek', 'lastWeek', 'recoveryRunId', 'generatedAt',
      ],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

/**
 * `/postseason/lost-sales` — the Census page's table: the season's latest recovery run, its
 * store-weeks per availability state in the vocabulary's order, every published state present
 * (a state the run did not count leaves as missing, never 0), and whether it enters the
 * likelihood. Per run, never summed across runs (client rule 3 split, ADR 8001).
 */
export async function lostSalesCensusExport(): Promise<ExportRequest> {
  const season = requireSeason();
  const latest = await callOperation(latestRecoveryRun, { query: { season } });
  const runId = latest.hasRun ? latest.recoveryRunId : '';
  const [census, states] = await Promise.all([
    collectPages(listStateCensus, { query: { season } }),
    callOperation(vocabulary, {}),
  ]);
  const lines = runId === ''
    ? []
    : censusLines(census.filter((row) => row.recoveryRunId === runId), states.planningStates, states.modelledStates);
  const rows: ExportCell[][] = lines.map((line) => [
    { kind: 'text', value: line.state },
    integer(line.storeWeeks),
    { kind: 'text', value: line.isModelled ? 'enters_likelihood' : 'excluded_from_likelihood' },
  ]);
  return {
    id: 'postseason.lostSales.census',
    titleKey: 'postseason.lostSales.censusTitle',
    columns: [
      { key: 'state', headerKey: 'postseason.column.state', descriptionKey: 'postseason.columnGuide.census.state', kind: 'text' },
      { key: 'storeWeeks', headerKey: 'postseason.unit.storeWeeks', descriptionKey: 'postseason.columnGuide.census.storeWeeks', kind: 'integer', unit: 'store-weeks' },
      { key: 'inLikelihood', headerKey: 'postseason.column.inLikelihood', descriptionKey: 'postseason.columnGuide.census.inLikelihood', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [{
        measure: 'state_census',
        definition: 'Store-weeks the recovery run counted in each availability state (reqs/00 D8). A state the run did not count is missing, never zero.',
      }],
      [
        { field: 'season', operator: 'equals', value: season },
        { field: 'recovery_run_id', operator: 'equals', value: runId },
      ],
    ),
    perimeter: {
      filters: [{ field: 'season', operator: 'equals', value: season }],
      sort: [],
      visibleColumns: ['state', 'storeWeeks', 'inLikelihood'],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

/** A potential class's figure as a cell: the units, or missing — never 0, and never a sum of runs. */
function potentialCell(figure: PotentialFigure): ExportCell {
  if (figure.kind === 'severalRuns') return { kind: 'missing', state: 'not-ranged' };
  return integer(figureValue(figure));
}

/**
 * `/postseason/lost-sales-potential` — assortment and late-ranging potential per door, one
 * column per class and never a total (bata-platform ADR 8003, 8004), the doors a `?store=` scope
 * names. A door where two effective runs estimated one class leaves that class missing
 * (`not-ranged`), as the page shows it as neither.
 */
export async function lostSalesPotentialExport(): Promise<ExportRequest> {
  const season = requireSeason();
  const scope = readLostSalesScope(typeof window === 'undefined' ? '' : window.location.search);
  const lines = doorsInScope(potentialLines(await collectPages(listLostSalesPotential, { query: { season } })), scope.stores);
  const rows: ExportCell[][] = lines.map((line) => [
    { kind: 'text', value: line.locationKey },
    potentialCell(line.assortment),
    potentialCell(line.lateRanging),
  ]);
  return {
    id: 'postseason.lostSales.potential',
    titleKey: 'postseason.lostSales.potentialTitle',
    columns: [
      { key: 'locationKey', headerKey: 'postseason.column.location', descriptionKey: 'postseason.columnGuide.potential.locationKey', kind: 'text' },
      { key: 'assortmentPotentialUnits', headerKey: 'postseason.column.assortmentPotential', descriptionKey: 'postseason.columnGuide.potential.assortment', kind: 'integer', unit: 'units' },
      { key: 'lateRangingPotentialUnits', headerKey: 'postseason.column.lateRangingPotential', descriptionKey: 'postseason.columnGuide.potential.lateRanging', kind: 'integer', unit: 'units' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [{
        measure: 'lost_sales_potential',
        definition: 'Demand the stock-out recovery does not count, per door and class: assortment (an article the door never offered) and late ranging (the weeks before the door first offered one its cluster already had). One column per class; never summed.',
      }],
      [{ field: 'season', operator: 'equals', value: season }, ...scopeFilters({ ...scope, weeks: [], articles: [] })],
    ),
    perimeter: {
      filters: [{ field: 'season', operator: 'equals', value: season }, ...scopeFilters({ ...scope, weeks: [], articles: [] })],
      sort: [{ key: 'locationKey', direction: 'asc' }],
      visibleColumns: ['locationKey', 'assortmentPotentialUnits', 'lateRangingPotentialUnits'],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

export async function accuracyExport(): Promise<ExportRequest> {
  return accuracyRequest(await collectPages(listAccuracy, { query: {} }));
}

/** The accuracy sheet over rows already read, in the order given — the table's view (`docs/adr/9008`). */
export function accuracyRequest(data: readonly AccuracyRow[]): ExportRequest {
  const rows: ExportCell[][] = data.map((row) => [
    // The composite dimension key, as the opaque tuple the contract publishes. There is
    // no owner column and no planner column, and neither is invented here — see
    // docs/adr/0071.
    { kind: 'text', value: accuracyCategoryLabel(row) },
    { kind: 'integer', raw: row.observations },
    numeric(row.weightedAbsolutePercentageError),
    numeric(row.bias),
    numeric(row.mase),
    numeric(row.trackingSignal),
    numeric(row.forecastValueAdded),
  ]);
  return {
    id: 'postseason.accuracy.table',
    titleKey: 'postseason.accuracy.title',
    columns: [
      { key: 'dimensionKey', headerKey: 'postseason.column.dimension', descriptionKey: 'postseason.columnGuide.accuracy.dimension', kind: 'text' },
      { key: 'observations', headerKey: 'postseason.column.observations', descriptionKey: 'postseason.columnGuide.accuracy.observations', kind: 'integer', unit: 'count', polarity: 'higher-is-better' },
      { key: 'wape', headerKey: 'postseason.measure.wape', descriptionKey: 'postseason.columnGuide.accuracy.wape', kind: 'number', unit: 'ratio', polarity: 'lower-is-better' },
      { key: 'bias', headerKey: 'postseason.measure.bias', descriptionKey: 'postseason.columnGuide.accuracy.bias', kind: 'number', unit: 'ratio', polarity: 'target-relative' },
      { key: 'mase', headerKey: 'postseason.measure.mase', descriptionKey: 'postseason.columnGuide.accuracy.mase', kind: 'number', unit: 'index', polarity: 'lower-is-better' },
      { key: 'trackingSignal', headerKey: 'postseason.measure.trackingSignal', descriptionKey: 'postseason.columnGuide.accuracy.trackingSignal', kind: 'number', unit: 'index', polarity: 'target-relative' },
      { key: 'forecastValueAdded', headerKey: 'postseason.measure.fva', descriptionKey: 'postseason.columnGuide.accuracy.forecastValueAdded', kind: 'number', unit: 'percentagePoints', polarity: 'higher-is-better' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [
        {
          measure: 'forecast_value_added',
          definition:
            'Reduction in weighted absolute percentage error against a named baseline, in percentage points.',
        },
        {
          measure: 'forecast_bias',
          definition: 'Signed forecast error as a share of actual. Positive is over-forecast.',
        },
      ],
      // The accuracy warehouse is not season-bounded in the contract, so the export does
      // not claim a season filter it did not apply.
      [{ field: 'mape', operator: 'equals', value: 'excluded_by_design' }],
    ),
    perimeter: {
      filters: [],
      sort: [{ key: 'wape', direction: 'desc' }],
      visibleColumns: [
        'dimensionKey', 'observations', 'wape', 'bias', 'mase', 'trackingSignal',
        'forecastValueAdded',
      ],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

export async function attributeExport(): Promise<ExportRequest> {
  return attributeRequest(await collectPages(listAttributeIndices, { query: {} }));
}

/** The attribute-index sheet over rows already read, in the order given — the table's view (`docs/adr/9008`). */
export function attributeRequest(data: readonly AttributeIndexRow[]): ExportRequest {
  const rows: ExportCell[][] = data.map((row) => [
    { kind: 'text', value: row.attribute },
    { kind: 'text', value: row.value },
    { kind: 'text', value: row.clusterKey },
    { kind: 'text', value: row.versionId },
    { kind: 'integer', raw: row.options },
    numeric(row.demandRate),
    numeric(row.relativeDemandIndex ?? null),
    numeric(row.fullPriceSellThroughRate),
    numeric(row.realisedGrossMarginRate),
    { kind: 'text', value: row.isSignificant ? 'weightable' : 'thin' },
  ]);
  return {
    id: 'postseason.attributes.table',
    titleKey: 'postseason.attributes.title',
    columns: [
      { key: 'attribute', headerKey: 'postseason.column.attribute', descriptionKey: 'postseason.columnGuide.attributes.attribute', kind: 'text' },
      { key: 'value', headerKey: 'postseason.column.value', descriptionKey: 'postseason.columnGuide.attributes.value', kind: 'text' },
      { key: 'clusterKey', headerKey: 'postseason.column.cluster', descriptionKey: 'postseason.columnGuide.attributes.clusterKey', kind: 'text' },
      { key: 'versionId', headerKey: 'postseason.column.version', descriptionKey: 'postseason.columnGuide.attributes.versionId', kind: 'text' },
      { key: 'options', headerKey: 'postseason.column.options', descriptionKey: 'postseason.columnGuide.attributes.options', kind: 'integer', unit: 'count', polarity: 'higher-is-better' },
      { key: 'demandRate', headerKey: 'postseason.measure.demandRate', descriptionKey: 'postseason.columnGuide.attributes.demandRate', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'relativeDemandIndex', headerKey: 'postseason.measure.attributeIndex', descriptionKey: 'postseason.columnGuide.attributes.relativeDemandIndex', kind: 'number', unit: 'index', polarity: 'higher-is-better' },
      { key: 'fullPriceSellThroughRate', headerKey: 'postseason.measure.fullPriceSellThrough', descriptionKey: 'postseason.columnGuide.attributes.fullPriceSellThrough', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'realisedGrossMarginRate', headerKey: 'postseason.measure.realisedGrossMargin', descriptionKey: 'postseason.columnGuide.attributes.realisedGrossMargin', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'isSignificant', headerKey: 'postseason.column.evidence', descriptionKey: 'postseason.columnGuide.attributes.evidence', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [
        {
          measure: 'attribute_demand_rate',
          definition:
            'Unconstrained demand per option-week of distribution for one attribute value, at the published version.',
        },
      ],
      [],
    ),
    perimeter: {
      filters: [],
      sort: [{ key: 'demandRate', direction: 'desc' }],
      visibleColumns: [
        'attribute', 'value', 'clusterKey', 'versionId', 'options', 'demandRate', 'relativeDemandIndex',
        'fullPriceSellThroughRate', 'realisedGrossMarginRate', 'isSignificant',
      ],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

export async function supplierExport(): Promise<ExportRequest> {
  const season = requireSeason();
  return supplierRequest(await collectPages(listSupplierScorecards, { query: { season } }), season);
}

/**
 * The scorecard's sheet over rows already read, in the order given — the CSV under the
 * ranked scorecard hands in `rankSuppliers`' order, which is the sort the perimeter below
 * has always claimed (`docs/adr/9008`).
 */
export function supplierRequest(data: readonly SupplierScorecardRow[], season: string): ExportRequest {
  const rows: ExportCell[][] = data.map((row) => [
    { kind: 'text', value: row.supplierKey },
    // The supplier's name as the screen shows it beside the key (usability audit B7); a name the
    // platform does not send is not reported, never the key standing in for it.
    row.supplierName === null || row.supplierName === undefined || row.supplierName.trim() === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'text', value: row.supplierName },
    numeric(row.onTimeRate),
    numeric(row.inFullRate),
    numeric(row.otifRate),
    numeric(row.fillRate),
    numeric(row.leadTimeMeanDays),
    numeric(row.leadTimeVarianceDays),
    numeric(row.defectRate),
    numeric(row.inspectionCoverage),
    numeric(row.firstPassYield),
    numeric(row.compositeScore),
  ]);
  return {
    id: 'postseason.suppliers.table',
    titleKey: 'postseason.suppliers.title',
    columns: [
      { key: 'supplierKey', headerKey: 'postseason.column.supplier', descriptionKey: 'postseason.columnGuide.suppliers.supplierKey', kind: 'text' },
      { key: 'supplierName', headerKey: 'postseason.column.supplierName', descriptionKey: 'postseason.columnGuide.suppliers.supplierName', kind: 'text' },
      { key: 'onTimeRate', headerKey: 'postseason.measure.onTime', descriptionKey: 'postseason.columnGuide.suppliers.onTime', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'inFullRate', headerKey: 'postseason.measure.inFull', descriptionKey: 'postseason.columnGuide.suppliers.inFull', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'otifRate', headerKey: 'postseason.measure.otif', descriptionKey: 'postseason.columnGuide.suppliers.otif', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'fillRate', headerKey: 'postseason.measure.fillRate', descriptionKey: 'postseason.columnGuide.suppliers.fillRate', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'leadTimeMeanDays', headerKey: 'postseason.measure.leadTimeMean', descriptionKey: 'postseason.columnGuide.suppliers.leadTimeMean', kind: 'number', unit: 'days', polarity: 'lower-is-better' },
      { key: 'leadTimeVarianceDays', headerKey: 'postseason.measure.leadTimeVariance', descriptionKey: 'postseason.columnGuide.suppliers.leadTimeVariance', kind: 'number', unit: 'days', polarity: 'lower-is-better' },
      { key: 'defectRate', headerKey: 'postseason.measure.defectRate', descriptionKey: 'postseason.columnGuide.suppliers.defectRate', kind: 'number', unit: 'ratio', polarity: 'lower-is-better' },
      { key: 'inspectionCoverage', headerKey: 'postseason.measure.inspectionCoverage', descriptionKey: 'postseason.columnGuide.suppliers.inspectionCoverage', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'firstPassYield', headerKey: 'postseason.measure.firstPassYield', descriptionKey: 'postseason.columnGuide.suppliers.firstPassYield', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'compositeScore', headerKey: 'postseason.measure.composite', descriptionKey: 'postseason.columnGuide.suppliers.composite', kind: 'number', unit: 'index', polarity: 'higher-is-better' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [
        {
          measure: 'supplier_otif_rate',
          definition:
            'Share of received PO lines both on time and in full, evaluated line by line rather than as the product of two independently computed rates.',
        },
      ],
      [{ field: 'season', operator: 'equals', value: season }],
    ),
    perimeter: {
      filters: [{ field: 'season', operator: 'equals', value: season }],
      sort: [{ key: 'compositeScore', direction: 'desc' }],
      visibleColumns: [
        'supplierKey', 'supplierName', 'onTimeRate', 'inFullRate', 'otifRate', 'fillRate',
        'leadTimeMeanDays', 'leadTimeVarianceDays', 'defectRate', 'inspectionCoverage',
        'firstPassYield', 'compositeScore',
      ],
      drillPath: ['SOURCING'],
      densityMode: 'comfortable',
    },
  };
}

export async function governanceExport(): Promise<ExportRequest> {
  const [refreshes, promotions] = await Promise.all([
    collectPages(listRefreshes, { query: {} }),
    collectPages(listPromotions, { query: {} }),
  ]);
  const rows: ExportCell[][] = [
    ...refreshes.map((row): ExportCell[] => [
      { kind: 'text', value: row.schemeId },
      { kind: 'text', value: row.versionId },
      { kind: 'integer', raw: row.storesCompared },
      numeric(row.retainedShare),
      numeric(row.curveDivergence),
      // An unsigned refresh exports as a missing state, not as an empty string: reqs/06
      // §3.5 makes the signature the thing that permits adoption, and a blank cell in a
      // spreadsheet reads as "not applicable".
      row.signedBy === null || row.signedBy === ''
        ? { kind: 'missing', state: 'not-reported' }
        : { kind: 'text', value: row.signedBy },
      { kind: 'text', value: row.adoptionState },
    ]),
    ...promotions.map((row): ExportCell[] => [
      { kind: 'text', value: row.modelId },
      { kind: 'text', value: row.challengerVersionId },
      { kind: 'integer', raw: row.windowsEvaluated },
      { kind: 'missing', state: 'not-ranged' },
      { kind: 'missing', state: 'not-ranged' },
      { kind: 'missing', state: 'not-ranged' },
      { kind: 'text', value: row.outcomeCode },
    ]),
  ];
  return {
    id: 'postseason.governance.table',
    titleKey: 'postseason.governance.title',
    columns: [
      { key: 'subject', headerKey: 'postseason.column.subject', descriptionKey: 'postseason.columnGuide.governance.subject', kind: 'text' },
      { key: 'versionId', headerKey: 'postseason.column.version', descriptionKey: 'postseason.columnGuide.governance.version', kind: 'text' },
      { key: 'evidence', headerKey: 'postseason.column.evidence', descriptionKey: 'postseason.columnGuide.governance.evidence', kind: 'integer', unit: 'count', polarity: 'higher-is-better' },
      { key: 'retainedShare', headerKey: 'postseason.measure.retainedShare', descriptionKey: 'postseason.columnGuide.refresh.retainedShare', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'curveDivergence', headerKey: 'postseason.measure.curveDivergence', descriptionKey: 'postseason.columnGuide.refresh.curveDivergence', kind: 'number', unit: 'index', polarity: 'lower-is-better' },
      { key: 'signedBy', headerKey: 'postseason.column.signedBy', descriptionKey: 'postseason.columnGuide.refresh.signedBy', kind: 'text' },
      { key: 'outcome', headerKey: 'postseason.column.outcome', descriptionKey: 'postseason.columnGuide.governance.outcome', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(
      [
        {
          measure: 'cluster_membership_stability_rate',
          definition:
            'Share of clusterable stores whose cluster assignment is unchanged between two consecutive refreshes of the same scheme.',
        },
      ],
      [{ field: 'scope', operator: 'equals', value: 'all_schemes_and_models' }],
    ),
    perimeter: {
      filters: [],
      sort: [{ key: 'subject', direction: 'asc' }],
      visibleColumns: [
        'subject', 'versionId', 'evidence', 'retainedShare', 'curveDivergence',
        'signedBy', 'outcome',
      ],
      drillPath: [],
      densityMode: 'comfortable',
    },
  };
}

const GOVERNANCE_MEASURES = [
  {
    measure: 'cluster_membership_stability_rate',
    definition:
      'Share of clusterable stores whose cluster assignment is unchanged between two consecutive refreshes of the same scheme.',
  },
] as const;

/**
 * The cluster refreshes on their own — the CSV under the refresh table (`docs/adr/9008`).
 * The route's export folds refreshes and promotions into one sheet; a download under ONE of
 * the two tables hands over that table, with the columns it draws.
 */
export function refreshesRequest(refreshes: readonly RefreshRow[]): ExportRequest {
  const rows: ExportCell[][] = refreshes.map((row) => [
    { kind: 'text', value: row.schemeId },
    { kind: 'text', value: row.versionId },
    { kind: 'integer', raw: row.storesCompared },
    integer(row.retainedStores),
    numeric(row.retainedShare),
    numeric(row.curveDivergence),
    row.signedBy === null || row.signedBy === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'text', value: row.signedBy },
    { kind: 'text', value: row.adoptionState },
  ]);
  return {
    id: 'postseason.governance.refreshes',
    titleKey: 'postseason.governance.title',
    columns: [
      { key: 'schemeId', headerKey: 'postseason.column.scheme', descriptionKey: 'postseason.columnGuide.refresh.schemeId', kind: 'text' },
      { key: 'versionId', headerKey: 'postseason.column.version', descriptionKey: 'postseason.columnGuide.refresh.versionId', kind: 'text' },
      { key: 'storesCompared', headerKey: 'postseason.column.storesCompared', descriptionKey: 'postseason.columnGuide.refresh.storesCompared', kind: 'integer', unit: 'count' },
      { key: 'storesRetained', headerKey: 'postseason.column.storesRetained', descriptionKey: 'postseason.columnGuide.refresh.storesRetained', kind: 'integer', unit: 'count' },
      { key: 'retainedShare', headerKey: 'postseason.measure.retainedShare', descriptionKey: 'postseason.columnGuide.refresh.retainedShare', kind: 'number', unit: 'ratio', polarity: 'higher-is-better' },
      { key: 'curveDivergence', headerKey: 'postseason.measure.curveDivergence', descriptionKey: 'postseason.columnGuide.refresh.curveDivergence', kind: 'number', unit: 'index', polarity: 'lower-is-better' },
      { key: 'signedBy', headerKey: 'postseason.column.signedBy', descriptionKey: 'postseason.columnGuide.refresh.signedBy', kind: 'text' },
      { key: 'outcome', headerKey: 'postseason.column.outcome', descriptionKey: 'postseason.columnGuide.refresh.outcome', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(GOVERNANCE_MEASURES, [{ field: 'scope', operator: 'equals', value: 'all_schemes' }]),
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: ['schemeId', 'versionId', 'storesCompared', 'storesRetained', 'retainedShare', 'curveDivergence', 'signedBy', 'outcome'],
      drillPath: ['PRODA-FOOTWEAR'],
      densityMode: 'comfortable',
    },
  };
}

/** The champion/challenger decisions on their own — the CSV under that table (`docs/adr/9008`). */
export function promotionsRequest(promotions: readonly PromotionRow[]): ExportRequest {
  const rows: ExportCell[][] = promotions.map((row) => [
    { kind: 'text', value: row.modelId },
    { kind: 'text', value: row.championVersionId },
    { kind: 'text', value: row.challengerVersionId },
    { kind: 'integer', raw: row.windowsEvaluated },
    { kind: 'text', value: row.promoted ? 'promoted' : 'not_promoted' },
    { kind: 'text', value: row.outcomeCode },
  ]);
  return {
    id: 'postseason.governance.promotions',
    titleKey: 'postseason.governance.title',
    columns: [
      { key: 'modelId', headerKey: 'postseason.column.model', descriptionKey: 'postseason.columnGuide.decision.modelId', kind: 'text' },
      { key: 'championVersionId', headerKey: 'postseason.column.champion', descriptionKey: 'postseason.columnGuide.decision.champion', kind: 'text' },
      { key: 'challengerVersionId', headerKey: 'postseason.column.challenger', descriptionKey: 'postseason.columnGuide.decision.challenger', kind: 'text' },
      { key: 'windowsEvaluated', headerKey: 'postseason.column.windows', descriptionKey: 'postseason.columnGuide.decision.windows', kind: 'integer', unit: 'count' },
      { key: 'decision', headerKey: 'postseason.column.decision', descriptionKey: 'postseason.columnGuide.decision.decision', kind: 'text' },
      { key: 'outcome', headerKey: 'postseason.column.outcome', descriptionKey: 'postseason.columnGuide.decision.outcome', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'model_promotion_decision',
        definition:
          'A champion/challenger comparison over the evaluated windows, and whether the challenger was promoted.',
      },
    ], [{ field: 'scope', operator: 'equals', value: 'all_models' }]),
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: ['modelId', 'championVersionId', 'challengerVersionId', 'windowsEvaluated', 'decision', 'outcome'],
      drillPath: ['PRODA-FOOTWEAR'],
      densityMode: 'comfortable',
    },
  };
}
